import api from '@/api';
import {
  ContentsContext,
  IndustryData,
  SearchData,
  TrackingBaseData,
} from '@/api/tracking';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import { AdpDocument, Comment, Mention } from '@/types';

type CommentCreateProps = {
  content: string;
  groupId: number;
  mention?: Mention;
  adpDocument?: AdpDocument;
  themeId?: number;
  parentComment?: Comment;
};

type CommentUpdateProps = {
  comment: Comment;
  content: string;
  adpDocument?: AdpDocument;
};

type CommentRemoveProps = {
  comment: Comment;
  adpDocument?: AdpDocument;
};

export const useComment = (
  props: {
    hideSnackbar?: boolean;
    // 計測
    trackingBaseData?: TrackingBaseData;
    rankInWholeFeed?: number;
    searchData?: SearchData;
    industryData?: IndustryData;
    trendCompanyText?: string;
    contentsContext?: ContentsContext;
    execution?: string;
  } = {},
) => {
  const { createSnackbar } = useSnackbar();

  const createComment = async ({
    content,
    groupId,
    mention,
    adpDocument,
    themeId,
    parentComment,
  }: CommentCreateProps) => {
    try {
      const createdComment = await api.sendComment({
        groupId: groupId,
        adpDocument: adpDocument,
        themeId: themeId,
        content: content,
        mention: mention,
        parentCommentId: parentComment?.id,
        trackingBaseData: props.trackingBaseData ?? {},
        rankInWholeFeed: props.rankInWholeFeed,
        searchData: props.searchData,
        industryData: props.industryData,
        description: props.trendCompanyText,
        contentsContext: props.contentsContext,
        execution: props.execution,
      });

      if (parentComment) {
        const adpDocumentParent = adpDocument?.comments.find(
          c => c.id === parentComment.id,
        );
        if (adpDocumentParent) {
          adpDocumentParent.children.push(createdComment);
        } else if (parentComment) {
          parentComment.children.push(createdComment);
        }
      } else {
        adpDocument?.comments.push(createdComment);
      }

      if (!props.hideSnackbar) {
        createSnackbar({
          message: 'コメントを投稿しました',
          type: 'success',
        });
      }
      return createdComment;
    } catch (e) {
      if (!props.hideSnackbar) {
        createSnackbar({
          message: 'コメントを投稿できませんでした',
          type: 'error',
        });
      }
      throw e;
    }
  };

  const updateComment = async ({
    comment,
    content,
    adpDocument,
  }: CommentUpdateProps) => {
    const updatedComment = await api.updateComment(comment.id, content);

    if (comment.parent_comment_id) {
      const adpDocumentParent = adpDocument?.comments.find(
        c => c.id === comment.parent_comment_id,
      );
      if (adpDocumentParent) {
        const index = adpDocumentParent.children.findIndex(
          c => c.id === comment.id,
        );
        if (index !== -1) {
          adpDocumentParent.children[index] = updatedComment;
        }
      }
    } else if (adpDocument) {
      const index = adpDocument.comments.findIndex(c => c.id === comment.id);
      if (index !== -1) {
        adpDocument.comments[index] = updatedComment;
      }
    }

    createSnackbar({
      message: 'コメントを編集しました',
      type: 'success',
    });

    return updatedComment;
  };

  const removeComment = async ({
    comment,
    adpDocument,
  }: CommentRemoveProps) => {
    try {
      await api.deleteComment(comment.id as number);

      if (comment.parent_comment_id) {
        const adpDocumentParent = adpDocument?.comments.find(
          c => c.id === comment.parent_comment_id,
        );
        if (adpDocumentParent) {
          adpDocumentParent.children = adpDocumentParent.children.filter(
            c => c.id !== comment.id,
          );
        }
      } else {
        if (adpDocument) {
          adpDocument.comments = adpDocument.comments.filter(
            c => c.id !== comment.id,
          );
        }
      }

      createSnackbar({
        message: 'コメントを削除しました',
        type: 'success',
      });
    } catch (e) {
      createSnackbar({
        message: 'コメントを削除できませんでした',
        type: 'error',
      });
      throw e;
    }
  };

  return { createComment, updateComment, removeComment };
};
